import { createAsyncThunk } from '@reduxjs/toolkit';
import { 
  setNotifications, 
  setLoading, 
  setError,
  setLoadingMore,
  addNotifications,
  setPagination,
  markAsRead,
} from './reducer';
import { getNotifictions, getNotifictionsById } from '../../helpers';

// Add this helper function
const extractNotificationsFromResponse = (response) => {
  if (!response) return { notifications: [], pagination: null };
  
  // If response has data.notifications and pagination (your current structure)
  if (response.data && Array.isArray(response.data.notifications) && response.data.pagination) {
    return {
      notifications: response.data.notifications,
      pagination: response.data.pagination
    };
  }
  
  // If response is directly an array (old structure)
  if (Array.isArray(response)) {
    return {
      notifications: response,
      pagination: null
    };
  }
  
  // Default fallback
  console.warn('Could not extract notifications from response:', response);
  return { notifications: [], pagination: null };
};

// Fetch first page of notifications
export const fetchNotifications = createAsyncThunk(
  'notification/fetchNotifications',
  async (page = 1, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      console.log('Fetching notifications page:', page);
      
      const response = await getNotifictions(`?page=${page}&limit=20`);
      console.log('API Response:', response);
      
      // Use the helper to safely extract notifications and pagination
      const { notifications, pagination } = extractNotificationsFromResponse(response);
      
      console.log('Processed notifications:', notifications);
      console.log('Pagination info:', pagination);
      
      dispatch(setNotifications(notifications));
      if (pagination) {
        dispatch(setPagination(pagination));
      }
      return { notifications, pagination };
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch notifications';
      dispatch(setError(errorMessage));
      return rejectWithValue(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Load more notifications
export const loadMoreNotifications = createAsyncThunk(
  'notification/loadMoreNotifications',
  async (page, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setLoadingMore(true));
      console.log('Loading more notifications, page:', page);
      
      const response = await getNotifictions(`?page=${page}&limit=20`);
      console.log('Load more API Response:', response);
      
      const { notifications, pagination } = extractNotificationsFromResponse(response);
      
      console.log('Loaded more notifications:', notifications);
      
      dispatch(addNotifications(notifications));
      if (pagination) {
        dispatch(setPagination(pagination));
      }
      return { notifications, pagination };
    } catch (error) {
      console.error('Error in loadMoreNotifications:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load more notifications';
      dispatch(setError(errorMessage));
      return rejectWithValue(errorMessage);
    } finally {
      dispatch(setLoadingMore(false));
    }
  }
);

// Mark notification as read thunk
export const markNotificationAsRead = createAsyncThunk(
  'notification/markAsRead',
  async (notificationId, { dispatch, rejectWithValue, getState }) => {
    try {
      // Optimistically update UI - mark as read immediately
      dispatch(markAsRead(notificationId));

      // Call your API helper to mark as read on server
      await getNotifictionsById(notificationId);

      // If API call succeeds, the optimistic update remains
      return { notificationId, success: true };
    } catch (error) {
      // Revert optimistic update if API call fails
      const state = getState();
      
      // Make sure we're working with an array
      const currentNotifications = Array.isArray(state.Notification.notifications) 
        ? state.Notification.notifications 
        : [];
        
      const originalNotifications = currentNotifications.map(notification => 
        notification.id === notificationId || notification._id === notificationId 
          ? { ...notification, isRead: false } 
          : notification
      );
      dispatch(setNotifications(originalNotifications));
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to mark notification as read';
      dispatch(setError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);